"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, ArrowRight, Phone, Mail, CircleCheck as CheckCircle2, Search, Sparkles } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "item-1",
  question: "Why does my Wigan business need local SEO instead of general UK SEO?",
  answer: "When customers in Wigan, Standish, or Leigh search for your services, Google prioritizes local results—especially the Map Pack. Local SEO ensures your website and Google Business Profile rank prominently when local buyers are ready to take action. As an independent SEO consultant, I tailor strategies specifically to capture high-intent search traffic across Greater Manchester and Lancashire.",
  category: "Strategy"
}, {
  id: "item-2",
  question: "Will I work directly with you, Yassine, or be passed to an account manager?",
  answer: "You work directly with me. Unlike traditional agencies in Manchester or Liverpool that assign junior staff to client accounts, I handle every technical audit, keyword strategy, content plan, and technical optimization myself. You get direct access to my senior SEO expertise throughout our engagement.",
  category: "Services"
}, {
  id: "item-3",
  question: "How long does it take to see measurable SEO results in the Wigan area?",
  answer: "Most Wigan businesses see initial local Map Pack improvements and organic position gains within 60 to 90 days. Competitive industries may require 4 to 6 months of targeted content and local citation building. I provide transparent monthly reporting so you can track exact position movements, organic traffic growth, and phone/form conversions.",
  category: "Results"
}, {
  id: "item-4",
  question: "What is included in your Wigan local SEO consultancy service?",
  answer: "My comprehensive local service includes a deep technical SEO audit, local competitor analysis, Google Business Profile optimization, hyper-local keyword research (targeting Wigan and surrounding towns), local citation cleanup, schema markup implementation, and strategic local link acquisition.",
  category: "Strategy"
}, {
  id: "item-5",
  question: "Can you help if my Wigan business serves surrounding towns like Leigh or Ormskirk?",
  answer: "Yes. I design multi-location and regional service-area SEO strategies. We create dedicated, non-spammy location landing pages and optimize geo-targeted schema markup to capture high-intent leads across Ashton-in-Makerfield, Skelmersdale, St Helens, Chorley, and the wider North West.",
  category: "Strategy"
}, {
  id: "item-6",
  question: "How do you optimize my Google Business Profile for local Wigan searchers?",
  answer: "I conduct a thorough audit of your Google Business Profile, optimizing primary and secondary categories, service lists, business descriptions, high-resolution geotagged photos, and Q&A features. I also establish a review acquisition system to boost your star rating and visibility in local map searches.",
  category: "Technical"
}, {
  id: "item-7",
  question: "How does your consultancy differ from hiring a local Wigan or Manchester SEO agency?",
  answer: "Agencies often lock you into rigid contracts with bloated monthly packages, overhead costs, and account managers acting as middlemen. Working with me means lower overheads, agile execution, flexible terms, and customized SEO strategies built around your commercial goals—not template-driven checklists.",
  category: "Services"
}, {
  id: "item-8",
  question: "Do you offer AI Search Optimization for Wigan businesses?",
  answer: "Yes. As Google evolves toward AI-driven search experiences (AI Overviews and Gemini), I optimize your digital presence for entity authority and semantic context. This ensures your Wigan business remains visible in both traditional organic listings and generative AI answers.",
  category: "Technical"
}, {
  id: "item-9",
  question: "What web platforms do you work with for SEO implementations?",
  answer: "I work seamlessly with WordPress, Shopify, Webflow, WooCommerce, Custom PHP/React builds, and Wix. Whether you need hands-on technical fixes or recommendations for your internal developer, I deliver clear, actionable instructions.",
  category: "Technical"
}, {
  id: "item-10",
  question: "How do local backlinks and local citations impact my local rankings?",
  answer: "Google relies on local signals to verify your business prominence and location accuracy. I build genuine local authority through regional directory citations, local news mentions, sponsorship strategies, and high-quality North West business links—never spammy link farms.",
  category: "Strategy"
}, {
  id: "item-11",
  question: "What happens during a free initial SEO consultation?",
  answer: "During our 30-minute consultation, I analyze your current organic rankings, review your local visibility against Wigan competitors, and highlight key growth opportunities. There is zero pressure or pushy sales tactics—just honest, expert advice.",
  category: "Results"
}, {
  id: "item-12",
  question: "How do I get started with your local SEO services?",
  answer: "Getting started is simple. Book a free consultation through my calendar, or send me an email detailing your website and business goals. I will follow up with an initial review and recommended roadmap for your business.",
  category: "Results"
}];
export default function WiganFaq() {
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const categories = ["All", "Strategy", "Services", "Technical", "Results"];
  const filteredFaqs = selectedCategory === "All" ? faqList : faqList.filter(faq => faq.category === selectedCategory);
  return <section id="wiganmarketfaq" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 transition-colors duration-300">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center space-y-4 mb-12 sm:mb-16">
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 px-3 py-1 rounded-full font-medium">
            <Sparkles className="size-3.5 text-primary" />
            <span>Wigan Local SEO FAQs</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Common Questions About Local SEO in Wigan
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto font-default leading-relaxed">
            Clear, honest answers about how I help Wigan and North West
            businesses improve local rankings, generate qualified leads, and grow organic revenue.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2 pt-4">
            {categories.map((category, i) => <Button key={category} data-index={i} variant={selectedCategory === category ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(category)} className={`rounded-full text-xs sm:text-sm font-medium transition-[color,background-color,border-color] duration-200 ${selectedCategory === category ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-background text-muted-foreground hover:text-foreground hover:bg-muted"}`}>
                {category}
              </Button>)}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 lg:p-10 shadow-sm">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {filteredFaqs.map((faq, i) => <motion.div key={faq.id} data-index={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: i * 0.04
          }}>
                <AccordionItem value={faq.id} className="border border-border/80 rounded-xl px-4 sm:px-6 bg-background/50 hover:bg-muted/30 transition-[background-color,border-color] duration-200">
                  <AccordionTrigger className="py-5 text-left hover:no-underline group">
                    <div className="flex items-start gap-3 sm:gap-4 pr-2">
                      <HelpCircle className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="font-semibold text-base sm:text-lg text-primary group-hover:text-primary/80 transition-colors font-default">
                        {faq.question}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 pl-8 sm:pl-9 text-muted-foreground text-sm sm:text-base leading-relaxed font-default">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>
        </div>

        <div className="mt-12 sm:mt-16 bg-muted/50 border border-border rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
              <CheckCircle2 className="size-4" />
              <span>Direct Consultant Access &bull; No Agency Middlemen</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground font-default">
              Have a specific question about your Wigan rankings?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base max-w-xl font-default">
              {"Book a direct 1-on-1 SEO consultation with me, Yassine Essaadi, to review your current website performance."}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Link to="/ContactPage" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 rounded-lg">
                <span>Book Free Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Button>
            </Link>
            <a href="mailto:contact@ye-consultancy.com" className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-background/80 font-medium px-5 rounded-lg">
                <Mail className="size-4 mr-2 text-muted-foreground" />
                <span>Email Yassine</span>
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>;
}